import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus, ArrowRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/Tooltip";

interface ButtonType {
  value: string;
  label: string;
  sublabel?: string;
  icon: any;
  maxCount: number;
  disabled?: boolean;
  disabledReason?: string;
}

interface AddButtonDropdownProps {
  availableTypes: ButtonType[];
  onSelect: (type: string) => void;
  disabled?: boolean;
  testId?: string;
}

export const AddButtonDropdown = ({ availableTypes, onSelect, disabled, testId }: AddButtonDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Calculate if we should drop up or down
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // Threshold of 400px (buttons dropdown is taller)
        setDropUp(spaceBelow < 400 && spaceAbove > spaceBelow);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const customTypes = availableTypes.filter(t => t.value === 'quick_reply');
  const ctaTypes = availableTypes.filter(t => t.value !== 'quick_reply');

  const handleSelect = (type: string) => {
    onSelect(type);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled && availableTypes.length > 0 && !availableTypes.every(t => t.disabled)) {
      setIsOpen(!isOpen);
    }
  };

  const isAllDisabled = availableTypes.every(t => t.disabled);

  if (availableTypes.length === 0) {
    return (
      <button
        type="button"
        disabled
        className="px-4 py-2 border border-ui-hairline rounded-sm bg-ui-surface-soft text-ui-muted cursor-not-allowed flex items-center gap-2 text-sm font-normal h-10 shadow-none"
      >
        <Plus className="w-4 h-4" />
        No buttons available
      </button>
    );
  }

  const renderOption = (type: ButtonType, isCta: boolean) => {
    const IconComponent = type.icon;
    const isDisabled = type.disabled;
    const isHovered = hoveredValue === type.value;

    const content = (
      <button
        type="button"
        onMouseEnter={() => !isDisabled && setHoveredValue(type.value)}
        onMouseLeave={() => setHoveredValue(null)}
        onClick={() => !isDisabled && handleSelect(type.value)}
        disabled={isDisabled}
        data-testid={testId ? `${testId}-option-${type.value}` : undefined}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 transition-all text-left group relative border-transparent outline-none text-sm font-normal',
          isDisabled
            ? 'opacity-50 cursor-not-allowed grayscale'
            : 'hover:bg-ui-surface-soft/50 text-ui-body hover:text-ui-ink'
        )}
      >
        <div className={cn(
          'shrink-0 w-8 h-8 rounded-sm flex items-center justify-center',
          isDisabled 
            ? 'bg-ui-surface-soft text-ui-muted' 
            : isCta 
              ? 'bg-purple-500/10 text-purple-650 dark:text-purple-400' 
              : 'bg-ui-primary-light text-ui-primary'
        )}>
          <IconComponent className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className={cn(
            'text-sm font-normal truncate',
            isDisabled ? 'text-ui-muted' : 'text-ui-ink group-hover:text-ui-primary'
          )}>
            {type.label}
          </div>
          {type.sublabel && (
            <div className="text-[10px] leading-tight text-ui-muted mt-0.5">
              {type.sublabel}
            </div>
          )}
        </div>

        {!isDisabled && isHovered && (
          <ArrowRight className="w-4 h-4 text-ui-primary animate-in fade-in slide-in-from-left-2 duration-200" />
        )}
      </button>
    );

    if (isDisabled && type.disabledReason) {
      return (
        <Tooltip key={type.value}>
          <TooltipTrigger asChild>
            <div className="w-full cursor-not-allowed">
              {content}
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            {type.disabledReason}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={type.value}>{content}</div>;
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || isAllDisabled}
        data-testid={testId}
        className={cn(
          'px-4 py-2 border rounded-sm bg-ui-surface-card text-left text-ui-ink transition-all flex items-center justify-between gap-3 text-sm font-normal shadow-none h-10',
          disabled || isAllDisabled
            ? 'opacity-50 border-ui-hairline cursor-not-allowed bg-ui-surface-soft'
            : isOpen
              ? 'border-ui-primary focus:ring-1 focus:ring-ui-primary/20'
              : 'border-ui-hairline hover:border-ui-primary/50'
        )}
      >
        <div className="flex items-center gap-2">
          <Plus className={cn('w-4 h-4', isAllDisabled ? 'text-ui-muted' : 'text-ui-success')} />
          <span>{isAllDisabled ? 'Max reached' : 'Add button'}</span>
        </div>
        <ChevronDown className="w-4 h-4 text-ui-muted transition-transform duration-200" />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute left-0 sm:left-auto sm:right-0 bg-ui-surface-card rounded-sm shadow-none border border-ui-hairline overflow-hidden z-[50] w-[min(300px,calc(100vw-1rem))] animate-in fade-in duration-300',
            dropUp
              ? 'bottom-full mb-4 slide-in-from-bottom-2'
              : 'top-full mt-4 slide-in-from-top-2'
          )}
        >
          {/* Custom Section Header */}
          {customTypes.length > 0 && (
            <div className="px-4 py-2 bg-ui-surface-soft/50 border-b border-ui-hairline-soft">
              <span className="text-xs font-semibold text-ui-muted uppercase tracking-wider">
                Quick Reply
              </span>
            </div>
          )}

          {/* Custom Section */}
          <div className="py-1">
            {customTypes.map(type => renderOption(type, false))}
          </div>

          {/* Call to Action Section Header */}
          {ctaTypes.length > 0 && (
            <div className="px-4 py-2 bg-ui-surface-soft/50 border-t border-b border-ui-hairline-soft">
              <span className="text-xs font-semibold text-ui-muted uppercase tracking-wider">
                Call to Action
              </span>
            </div>
          )}

          {/* Call to Action Section */}
          <div className="py-1">
            {ctaTypes.map(type => renderOption(type, true))}
          </div>
        </div>
      )}
    </div>
  );
};
